/** @jsxImportSource @emotion/react */
import { useEffect, useRef } from "react";

import Typography from "@mui/material/Typography";

import { cellCSS, yearBubble, yearGrid } from "../styles/useDatePickerStyles";
import { YearViewProps } from "../types";

const MIN_YEAR = 2000;
const MAX_YEAR = 2050;

const YearView = ({
  year,
  currentYear,
  minDate,
  maxDate,
  onYearSelect
}: YearViewProps) => {
  const selectedYearRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedYearRef.current) {
      const scrollY = window.scrollY;
      selectedYearRef.current.scrollIntoView({ block: "center" });
      window.scrollTo(0, scrollY);
    }
  }, []);

  const isYearDisabled = (yearValue: number) => {
    const beforeMin = minDate && yearValue < minDate.year();
    const afterMax = maxDate && yearValue > maxDate.year();
    return Boolean(beforeMin || afterMax);
  };

  const isYearWithinBounds = (yearValue: number) => {
    if (minDate && yearValue < minDate.year()) return false;
    if (maxDate && yearValue > maxDate.year()) return false;
    return true;
  };

  const availableYears = Array.from(
    { length: MAX_YEAR - MIN_YEAR + 1 },
    (_, index) => MIN_YEAR + index
  ).filter(isYearWithinBounds);

  return (
    <div css={yearGrid}>
      {availableYears.map(yearOption => (
        <div
          key={yearOption}
          css={cellCSS}
          ref={yearOption === year ? selectedYearRef : undefined}
        >
          <Typography
            variant="body1Medium"
            css={yearBubble({
              isSelected: yearOption === year,
              isInRange: false,
              isDisabled: isYearDisabled(yearOption),
              isToday: yearOption === currentYear
            })}
            onClick={() =>
              !isYearDisabled(yearOption) && onYearSelect(yearOption)
            }
          >
            {yearOption}
          </Typography>
        </div>
      ))}
    </div>
  );
};

export default YearView;
